import { useEscapeKey } from '../../hooks/useEscapeKey.ts';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Lock,
  Globe,
  FolderKanban,
  Trash2,
  Crown,
  MoreHorizontal,
  MessageSquare,
  Video,
  Calendar,
  Edit3,
  UserPlus,
  Link2,
  LogIn,
  LogOut,
  AlertTriangle,
  ArrowUpDown,
  Code2,
  Palette,
  Cpu,
  Megaphone,
  ShieldCheck,
  Briefcase,
  Layers,
  X,
  ArrowRight,
} from 'lucide-react';
import { useTenant, Space, SpaceMember } from '../../context/TenantContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { CreateSpaceModal } from '../tenant/CreateSpaceModal.tsx';
import { MemberProfilePopover } from './MemberProfilePopover.tsx';
import { ScheduleTeamMeetingModal } from './ScheduleTeamMeetingModal.tsx';
import { TeamDetailHub } from './TeamDetailHub.tsx';

interface TeamsWorkspaceProps {
  selectedTeamId?: string | null;
  onSelectTeam?: (teamId: string | null) => void;
  onSelectProject?: (projectId: string) => void;
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  code: Code2,
  palette: Palette,
  cpu: Cpu,
  megaphone: Megaphone,
  shield: ShieldCheck,
  briefcase: Briefcase,
  layers: Layers,
  users: Users,
};

export const TeamsWorkspace: React.FC<TeamsWorkspaceProps> = ({
  selectedTeamId: propSelectedTeamId = null,
  onSelectTeam,
  onSelectProject,
}) => {
  const { spaces, currentTenant, reloadSpaces } = useTenant();
  const { user, token } = useAuth();
  const { onlineUserIds } = useRealtime();

  const isUserOnline = (userId?: string | null): boolean => {
    if (!userId || !onlineUserIds) return false;
    if (onlineUserIds instanceof Set) return onlineUserIds.has(userId);
    if (Array.isArray(onlineUserIds)) return (onlineUserIds as string[]).includes(userId);
    return false;
  };

  const [activeTeamId, setActiveTeamId] = useState<string | null>(propSelectedTeamId);
  const [initialHubTab, setInitialHubTab] = useState<'board' | 'boards' | 'members' | 'meetings'>(
    'board'
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState<'all' | 'my'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'members' | 'active_tasks' | 'overdue_tasks'>(
    'name'
  );

  // Modals & Popovers
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSpace, setEditingSpace] = useState<Space | null>(null);
  const [openMenuSpaceId, setOpenMenuSpaceId] = useState<string | null>(null);
  const [deletingSpace, setDeletingSpace] = useState<Space | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  useEscapeKey(Boolean(deletingSpace) && !isDeleting, () => setDeletingSpace(null));
  const [inspectedMember, setInspectedMember] = useState<{
    member: SpaceMember;
    team: Space;
  } | null>(null);
  const [schedulingTeam, setSchedulingTeam] = useState<{
    team: Space;
    oneOnOneMember?: SpaceMember | null;
  } | null>(null);

  useEffect(() => {
    setActiveTeamId(propSelectedTeamId || null);
  }, [propSelectedTeamId]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleGlobalClick = () => setOpenMenuSpaceId(null);
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const isAdmin = user?.role === 'admin';
  // What each role may do with teams is set on the Roles & Access screen
  const { can } = usePermissions();
  const canCreateTeam = can('team.create');
  const canEditAnyTeam = can('team.edit');
  const canDeleteTeam = can('team.delete');
  const isViewer = user?.role === 'viewer';
  const isGuest = user?.role === 'guest';

  // Complete Isolation for Guest
  if (isGuest) {
    return (
      <div className="p-10 text-center">
        <Lock className="w-10 h-10 text-rose-500 mx-auto mb-2" />
        <h2 className="text-lg font-bold text-[var(--text-primary)]">Access Restricted</h2>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Guest accounts do not have permission to view the Teams directory.
        </p>
      </div>
    );
  }

  const navigateToTeamHub = (
    teamId: string,
    tab: 'board' | 'boards' | 'members' | 'meetings' = 'board'
  ) => {
    setInitialHubTab(tab);
    setActiveTeamId(teamId);
    if (onSelectTeam) {
      onSelectTeam(teamId);
    } else {
      window.history.pushState({}, '', `/teams/${teamId}`);
    }
  };

  const handleBackToDirectory = () => {
    setActiveTeamId(null);
    if (onSelectTeam) {
      onSelectTeam(null);
    } else {
      window.history.pushState({}, '', '/teams');
    }
    reloadSpaces();
  };

  // If a specific team is selected, render the Team Detail Hub (/teams/:teamId)
  if (activeTeamId) {
    return (
      <TeamDetailHub
        spaceId={activeTeamId}
        initialTab={initialHubTab}
        onBack={handleBackToDirectory}
        onOpenProjectBoard={onSelectProject}
      />
    );
  }

  // Filter & Sort Teams
  const filteredTeams = spaces
    .filter((s) => {
      if (filterTab === 'my') {
        const isMyTeam =
          Boolean(s.isJoined) ||
          Boolean(s.memberRole) ||
          Boolean(s.members?.some((m) => m.id === user?.id));
        if (!isMyTeam) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = (s.name || '').toLowerCase().includes(q);
        const matchSlug = (s.slug || '').toLowerCase().includes(q);
        const matchDesc = (s.description || '').toLowerCase().includes(q);
        if (!matchName && !matchSlug && !matchDesc) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'members') {
        return (b.memberCount || 0) - (a.memberCount || 0);
      }
      if (sortBy === 'active_tasks') {
        return (b.activeTasksCount || 0) - (a.activeTasksCount || 0);
      }
      if (sortBy === 'overdue_tasks') {
        return (b.overdueTasksCount || 0) - (a.overdueTasksCount || 0);
      }
      return (a.name || '').localeCompare(b.name || '');
    });

  const handleJoinOrLeave = async (space: Space, action: 'join' | 'leave') => {
    if (isViewer || isGuest) return;
    try {
      const res = await fetch(`/api/spaces/${space.id}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        await reloadSpaces();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || `Failed to ${action} team`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleConfirmDeleteTeam = async () => {
    if (!deletingSpace || !canDeleteTeam) return;
    try {
      setIsDeleting(true);
      const res = await fetch(`/api/spaces/${deletingSpace.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        setDeletingSpace(null);
        await reloadSpaces();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to delete team');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 h-full overflow-y-auto">
      {/* Top Page Header - Compact & Clean without unnecessary subtext */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200/80 dark:border-surface-800 shadow-2xs">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-tr from-[#0162fc] to-[#4b2efa] text-white flex items-center justify-center shadow-2xs shrink-0">
            <Users className="w-3.5 h-3.5 stroke-[2.5]" />
          </div>
          <h2 className="text-sm sm:text-base font-extrabold text-surface-900 dark:text-surface-100 uppercase tracking-tight">
            Teams & Spaces
          </h2>
          <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-400 border border-brand-200/60 dark:border-brand-800/60">
            {spaces.length} {spaces.length === 1 ? 'Team' : 'Teams'}
          </span>
        </div>

        {/* + Create Team Button — Rendered ONLY if currentUser.role === 'admin' */}
        {canCreateTeam && (
          <button
            type="button"
            onClick={() => {
              setEditingSpace(null);
              setIsCreateModalOpen(true);
            }}
            className="px-3.5 py-1.5 rounded-full bg-[#121417] hover:bg-black text-white dark:bg-white dark:text-black text-[11px] font-bold shadow-sm flex items-center space-x-1.5 transition-all cursor-pointer shrink-0"
          >
            <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
            <span>Create Team</span>
          </button>
        )}
      </div>

      {/* Directory Search, Filter Tabs (All Teams | My Teams) & Sort Bar */}
      <div className="px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200/80 dark:border-surface-800 shadow-2xs flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[260px]">
          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search teams by name or @handle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {/* Directory Filter Pills: All Teams | My Teams */}
          <div className="inline-flex items-center p-0.5 rounded-xl bg-surface-100 dark:bg-surface-800 border border-surface-200/60 dark:border-surface-700">
            <button
              type="button"
              onClick={() => setFilterTab('all')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                filterTab === 'all'
                  ? 'bg-white dark:bg-surface-900 text-surface-900 dark:text-surface-100 shadow-2xs'
                  : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
              }`}
            >
              All Teams ({spaces.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterTab('my')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                filterTab === 'my'
                  ? 'bg-white dark:bg-surface-900 text-surface-900 dark:text-surface-100 shadow-2xs'
                  : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
              }`}
            >
              My Teams (
              {
                spaces.filter(
                  (s) =>
                    Boolean(s.isJoined) ||
                    Boolean(s.memberRole) ||
                    Boolean(s.members?.some((m) => m.id === user?.id))
                ).length
              }
              )
            </button>
          </div>
        </div>

        {/* Live Count + Sort Dropdown */}
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] text-surface-500 font-medium hidden sm:inline">
            Showing <strong className="text-surface-900 dark:text-surface-100">{filteredTeams.length}</strong> of{' '}
            {spaces.length} teams
          </span>

          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs">
            <ArrowUpDown className="w-3.5 h-3.5 text-brand-500 shrink-0" />
            <span className="text-[11px] font-bold text-surface-400">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="bg-transparent text-xs font-medium text-surface-700 dark:text-surface-300 focus:outline-none cursor-pointer"
            >
              <option value="name">Name (A-Z)</option>
              <option value="members">Member Count</option>
              <option value="active_tasks">Active Tasks</option>
              <option value="overdue_tasks">Overdue Tasks</option>
            </select>
          </div>
        </div>
      </div>

      {/* Teams Grid */}
      {filteredTeams.length === 0 ? (
        <div className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-2xl p-12 text-center space-y-3 shadow-2xs">
          <div className="w-12 h-12 rounded-2xl bg-brand-50 dark:bg-brand-950/40 text-brand-600 dark:text-brand-400 flex items-center justify-center mx-auto">
            <Users className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-surface-900 dark:text-surface-100">No teams found</h3>
          <p className="text-xs text-surface-500 dark:text-surface-400 max-w-md mx-auto">
            {filterTab === 'my'
              ? 'You have not joined any teams matching this filter yet. Switch to "All Teams" to browse open teams.'
              : 'Create a team to organize your department members, boards, and #team-channels.'}
          </p>
          {canCreateTeam && (
            <button
              type="button"
              onClick={() => {
                setEditingSpace(null);
                setIsCreateModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#121417] hover:bg-black text-white dark:bg-white dark:text-black text-xs font-bold shadow-sm transition-all cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Create Team</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredTeams.map((space) => {
            const TeamIcon = ICON_MAP[space.icon] || Users;
            const isTeamLead = space.isTeamLead || space.memberRole === 'lead';
            const canManageThisTeam = canEditAnyTeam;
            const isJoined =
              Boolean(space.isJoined) ||
              Boolean(space.memberRole) ||
              Boolean(space.members?.some((m) => m.id === user?.id));
            const members = space.members || [];
            const activeTasksCount = space.activeTasksCount ?? 0;
            const inReviewCount = space.inReviewTasksCount ?? 0;
            const overdueCount = space.overdueTasksCount ?? 0;
            const completionPct = space.completionPercent ?? 0;

            return (
              <div
                key={space.id}
                onClick={() => navigateToTeamHub(space.id, 'board')}
                className="bg-white dark:bg-surface-900 rounded-2xl p-5 border border-surface-200 dark:border-surface-800 shadow-card hover:shadow-elevated hover:border-brand-500/40 cursor-pointer transition-all flex flex-col justify-between group relative overflow-hidden"
              >
                {/* Top Color Accent Strip */}
                <div
                  className="absolute top-0 left-0 right-0 h-1.5 transition-all group-hover:h-2"
                  style={{ backgroundColor: space.color || '#0162fc' }}
                />

                <div className="pt-1">
                  {/* Card Header + Role-Gated Context Menu (⋯) */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-2xs shrink-0 transition-transform group-hover:scale-105"
                        style={{ backgroundColor: space.color || '#0162fc' }}
                      >
                        <TeamIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-bold text-base text-surface-900 dark:text-surface-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors truncate">
                            {space.name}
                          </h3>
                          {isTeamLead && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0"
                              title="You are a Team Lead of this team"
                            >
                              <Crown className="w-2.5 h-2.5" /> Lead
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-mono text-xs font-bold text-brand-600 dark:text-brand-400 px-1.5 py-0.2 rounded bg-brand-500/10">
                            @{space.slug}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.2 rounded-full text-[10px] font-bold ${
                              space.isPrivate
                                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            }`}
                          >
                            {space.isPrivate ? (
                              <>
                                <Lock className="w-2.5 h-2.5" /> Private
                              </>
                            ) : (
                              <>
                                <Globe className="w-2.5 h-2.5" /> Open
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Top-Right ⋯ Context Menu (Hidden for Viewer/Guest if no actions apply) */}
                    {!isViewer && (
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() =>
                            setOpenMenuSpaceId((prev) =>
                              prev === space.id ? null : space.id
                            )
                          }
                          className="p-1.5 rounded-lg text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors cursor-pointer"
                          title="Team Actions"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>

                        {openMenuSpaceId === space.id && (
                          <div className="absolute right-0 top-9 w-48 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-xl shadow-modal py-1.5 z-30 text-xs font-semibold animate-in fade-in zoom-in-95 duration-100">
                            {canManageThisTeam && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuSpaceId(null);
                                    setEditingSpace(space);
                                    setIsCreateModalOpen(true);
                                  }}
                                  className="w-full px-3.5 py-2 text-left flex items-center gap-2 text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 cursor-pointer"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-brand-500" />
                                  <span>Edit Team Info</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuSpaceId(null);
                                    navigateToTeamHub(space.id, 'members');
                                  }}
                                  className="w-full px-3.5 py-2 text-left flex items-center gap-2 text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 cursor-pointer"
                                >
                                  <UserPlus className="w-3.5 h-3.5 text-emerald-500" />
                                  <span>Manage Members</span>
                                </button>
                              </>
                            )}

                            {!isJoined && (!space.isPrivate || isAdmin) && (
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuSpaceId(null);
                                  handleJoinOrLeave(space, 'join');
                                }}
                                className="w-full px-3.5 py-2 text-left flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                              >
                                <LogIn className="w-3.5 h-3.5" />
                                <span>Join Team</span>
                              </button>
                            )}

                            {isJoined && (
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuSpaceId(null);
                                  handleJoinOrLeave(space, 'leave');
                                }}
                                className="w-full px-3.5 py-2 text-left flex items-center gap-2 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                              >
                                <LogOut className="w-3.5 h-3.5" />
                                <span>Leave Team</span>
                              </button>
                            )}

                            {/* Delete Team — Strictly Admin Only */}
                            {canDeleteTeam && (
                              <>
                                <div className="my-1 border-t border-surface-100 dark:border-surface-800" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuSpaceId(null);
                                    setDeletingSpace(space);
                                  }}
                                  className="w-full px-3.5 py-2 text-left flex items-center gap-2 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 cursor-pointer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete Team</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-xs text-surface-500 dark:text-surface-400 line-clamp-2 leading-relaxed mb-4 min-h-[2rem]">
                    {space.description || `Tasks and collaboration for the ${space.name} team.`}
                  </p>

                  {/* Interactive Member Avatar Stack */}
                  <div
                    className="flex items-center justify-between mb-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center -space-x-1.5">
                      {members.slice(0, 5).map((m) => {
                        const isOnline = isUserOnline(m.id) || m.id === user?.id;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setInspectedMember({ member: m, team: space })}
                            className={`relative w-7 h-7 rounded-full bg-gradient-to-br from-[#0162fc] to-[#4b2efa] text-white text-[11px] font-bold flex items-center justify-center transition-transform hover:scale-110 hover:z-10 cursor-pointer ${
                              m.spaceRole === 'lead'
                                ? 'ring-2 ring-amber-400'
                                : 'ring-2 ring-white dark:ring-surface-900'
                            }`}
                            title={`${m.fullName} (${
                              m.spaceRole === 'lead' ? 'Team Lead' : 'Member'
                            }) — Click for profile`}
                          >
                            {m.avatarUrl ? (
                              <img
                                src={m.avatarUrl}
                                alt={m.fullName}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              (m.fullName || 'U').charAt(0).toUpperCase()
                            )}
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border-1.5 border-white dark:border-surface-900 ${
                                isOnline ? 'bg-emerald-500' : 'bg-surface-400'
                              }`}
                            />
                            {m.spaceRole === 'lead' && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center">
                                <Crown className="w-1.5 h-1.5" />
                              </span>
                            )}
                          </button>
                        );
                      })}

                      {members.length > 5 && (
                        <button
                          type="button"
                          onClick={() => navigateToTeamHub(space.id, 'members')}
                          className="w-7 h-7 rounded-full bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 ring-2 ring-white dark:ring-surface-900 text-[9px] font-bold flex items-center justify-center hover:bg-brand-500 hover:text-white transition-colors cursor-pointer"
                        >
                          +{members.length - 5}
                        </button>
                      )}

                      {canManageThisTeam && (
                        <button
                          type="button"
                          onClick={() => navigateToTeamHub(space.id, 'members')}
                          className="w-7 h-7 rounded-full bg-surface-50 dark:bg-surface-800 hover:bg-brand-500 text-surface-400 hover:text-white border border-dashed border-surface-300 dark:border-surface-700 flex items-center justify-center transition-colors cursor-pointer ml-1"
                          title="Add / Manage Team Members"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    <span className="text-[11px] font-medium text-surface-500">
                      {space.memberCount || members.length}{' '}
                      {(space.memberCount || members.length) === 1 ? 'Member' : 'Members'}
                    </span>
                  </div>

                  {/* Live Task & Board Health Bar */}
                  <div className="p-3 rounded-xl bg-surface-50 dark:bg-surface-800/50 border border-surface-200/60 dark:border-surface-800/80 space-y-2 mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-1.5 text-[11px]">
                      <span className="inline-flex items-center gap-1 font-semibold text-surface-800 dark:text-surface-200">
                        <FolderKanban className="w-3.5 h-3.5 text-brand-500" />
                        {space.projectCount || 0}{' '}
                        {(space.projectCount || 0) === 1 ? 'Project' : 'Projects'}
                      </span>
                      <span className="text-surface-600 dark:text-surface-400 font-medium">
                        {activeTasksCount} Active
                      </span>
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold">
                        {inReviewCount} In Review
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded font-bold ${
                          overdueCount > 0
                            ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
                            : 'text-surface-400'
                        }`}
                      >
                        {overdueCount} Overdue
                      </span>
                    </div>

                    {/* Thin Team Completion Progress Bar */}
                    <div className="w-full h-1.5 rounded-full bg-surface-200 dark:bg-surface-700 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-brand-500 to-emerald-500 transition-all duration-300"
                        style={{ width: `${completionPct}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Card Footer: Open Team Board + Quick Collaboration Icons */}
                <div
                  className="pt-3 border-t border-surface-100 dark:border-surface-800/80 flex items-center justify-between gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => navigateToTeamHub(space.id, 'board')}
                    className="px-3.5 py-1.5 rounded-xl bg-brand-50 hover:bg-brand-500 text-brand-700 hover:text-white dark:bg-brand-950/40 dark:text-brand-300 dark:hover:bg-brand-600 dark:hover:text-white text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5 group-hover:bg-brand-500 group-hover:text-white"
                  >
                    <span>Open Team Board</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>

                  {/* Quick Collaboration Icons (Hidden for Guest & Viewer) */}
                  {!isViewer && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          window.dispatchEvent(
                            new CustomEvent('open-floating-chat', {
                              detail: {
                                channelId: space.chatChannelId || `chan-team-${space.id}`,
                                channelName: `#${space.slug}`,
                              },
                            })
                          )
                        }
                        className="p-1.5 rounded-xl bg-surface-50 hover:bg-brand-50 hover:text-brand-600 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-500 dark:text-surface-300 border border-surface-200 dark:border-surface-700 transition-colors cursor-pointer"
                        title={`Team Chat (#${space.slug})`}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                      </button>

                      {(isAdmin || isJoined) && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              window.dispatchEvent(
                                new CustomEvent('start-call', {
                                  detail: {
                                    channelId: space.chatChannelId || `chan-team-${space.id}`,
                                    channelName: `${space.name} Team Huddle`,
                                    callType: 'video',
                                  },
                                })
                              )
                            }
                            className="p-1.5 rounded-xl bg-surface-50 hover:bg-emerald-50 hover:text-emerald-600 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-500 dark:text-surface-300 border border-surface-200 dark:border-surface-700 transition-colors cursor-pointer"
                            title={`Start Instant ${space.name} Team Call`}
                          >
                            <Video className="w-3.5 h-3.5" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setSchedulingTeam({ team: space })}
                            className="p-1.5 rounded-xl bg-surface-50 hover:bg-purple-50 hover:text-purple-600 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-500 dark:text-surface-300 border border-surface-200 dark:border-surface-700 transition-colors cursor-pointer"
                            title={`Schedule ${space.name} Team Meeting`}
                          >
                            <Calendar className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Team Modal */}
      <CreateSpaceModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingSpace(null);
        }}
        editingSpace={editingSpace}
      />

      {/* Interactive Member Profile Popover */}
      {inspectedMember && (
        <MemberProfilePopover
          member={inspectedMember.member}
          teamName={inspectedMember.team.name}
          onClose={() => setInspectedMember(null)}
          onScheduleOneOnOne={(member) =>
            setSchedulingTeam({ team: inspectedMember.team, oneOnOneMember: member })
          }
          onViewAssignedTasks={() => {
            navigateToTeamHub(inspectedMember.team.id, 'board');
          }}
        />
      )}

      {/* Schedule Team Meeting Modal */}
      {schedulingTeam && (
        <ScheduleTeamMeetingModal
          isOpen={true}
          onClose={() => setSchedulingTeam(null)}
          team={schedulingTeam.team}
          initialOneOnOneMember={schedulingTeam.oneOnOneMember}
        />
      )}

      {/* Admin Delete Team Confirmation Modal */}
      {deletingSpace && canDeleteTeam && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setDeletingSpace(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-3xl w-full max-w-md p-6 shadow-modal space-y-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-surface-900 dark:text-surface-100">
                    Delete Team "{deletingSpace.name}"?
                  </h3>
                  <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                    This will archive the team and unlink its boards. Existing board tasks will remain safe.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDeletingSpace(null)}
                className="p-1 text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingSpace(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleConfirmDeleteTeam}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-sm disabled:opacity-50 cursor-pointer transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Confirm Delete Team'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsWorkspace;
